import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import {
  useDocument, useDocumentStatus, useExtractedKpis, useSavedKpis,
} from "../../DocSense/hooks/useDocSenseData";
import ProcessingTracker from "../../DocSense/components/ProcessingTracker";
import KpiReviewTable from "../../DocSense/components/KpiReviewTable";

/**
 * Wraps the existing DocSense review UI for one evidence document.
 * `docSenseId` on a DocumentDto is a DocSenseEntity.id — exactly what the
 * DocSense endpoints/hooks expect, so this reuses them as-is.
 */
const EvidenceReviewPanel = ({ docSenseId, readOnly = false, onBack }) => {
  const { data: doc } = useDocument(docSenseId);
  const isProcessing = doc && !["COMPLETED", "FAILED"].includes(doc.status);
  const { status: polledStatus } = useDocumentStatus(docSenseId, isProcessing);

  const [displayStatus, setDisplayStatus] = useState(null);
  const currentStatus = displayStatus || doc?.status;
  const isCompleted = currentStatus === "COMPLETED";

  const { data: extractedKpis, isLoading: extractedLoading, refetch: refetchExtracted } =
    useExtractedKpis(isCompleted ? docSenseId : null);
  const { data: savedKpis, isLoading: savedLoading, refetch: refetchSaved } = useSavedKpis(docSenseId);

  useEffect(() => { if (doc?.status) setDisplayStatus(doc.status); }, [doc?.status]);
  useEffect(() => {
    if (polledStatus?.status) {
      setDisplayStatus(polledStatus.status);
      if (polledStatus.status === "COMPLETED") refetchExtracted();
    }
  }, [polledStatus, refetchExtracted]);

  const handleKpisSaved = () => {
    refetchSaved();
    refetchExtracted();
  };

  if (!docSenseId) return null;

  return (
    <div style={{ marginTop: 20 }}>
      <ProcessingTracker status={currentStatus} errorMessage={doc?.errorMessage || polledStatus?.errorMessage} />

      {isCompleted ? (
        <KpiReviewTable
          documentId={docSenseId}
          extractedKpis={extractedKpis}
          extractedLoading={extractedLoading}
          savedKpis={savedKpis}
          savedLoading={savedLoading}
          onSaved={handleKpisSaved}
          onCancelReview={onBack ?? null}
          refetchExtracted={refetchExtracted}
          readOnly={readOnly}
        />
      ) : currentStatus !== "FAILED" && (
        <div style={{ background: "white", border: "1.5px solid #e2eaf0", borderRadius: 12, padding: "40px 24px", textAlign: "center", color: "#6b8794" }}>
          <Spinner animation="border" style={{ color: "#3f88a5", marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: "0.9rem" }}>KPIs will appear here once extraction is complete.</p>
        </div>
      )}
    </div>
  );
};

export default EvidenceReviewPanel;
